import { createStore } from "vuex";

import config from "@/app/config.js";
import router from "@/app/router";
import { DEFAULT_DATE_FORMAT, DEFAULT_TIME_FORMAT, DATE_FORMATS, TIME_FORMATS } from "@/lib/constants/dateTimeFormats";
import { mapLicenseError } from "@/lib/utils/licenseErrorMapper";
import * as apiKeyService from "@/services/apiKeyService";
import * as authService from "@/services/authService";
import * as exportService from "@/services/exportService";
import * as integrationService from "@/services/integrationService";
import * as licenceService from "@/services/licenceService";
import * as mediaService from "@/services/mediaService";
import * as miscService from "@/services/miscService";
import * as productService from "@/services/productService";
import * as submissionService from "@/services/submissionService";
import * as teamService from "@/services/teamService";
import * as userService from "@/services/userService";
import * as webhookService from "@/services/webhookService";
import axios from "../plugins/axios";
import { setSentryUser, clearSentryUser } from "../plugins/sentryHelpers";
import { toaster } from "../plugins/toaster";

import comments from "./modules/comments";
import list from "./modules/list";
import submission from "./modules/submission";
import upload from "./modules/upload";
import packageJson from "../../package.json";

// Cached initialization promise - ensures app only initializes once
let initPromise = null;

// Track when licence/usage data was last fetched to avoid serving stale data.
// The initPromise caches the entire init forever, but licence state can change
// server-side (expiry, renewal, limit changes). This ensures we re-fetch it
// periodically so users don't see stale licence status.
const LICENCE_STALE_THRESHOLD = 5 * 60 * 1000; // 5 minutes
let lastLicenceCheck = 0;
let lastUsageCheck = 0;

// Create a new store instance.
const store = createStore({
  state() {
    const hasToken = !!localStorage.getItem("token");

    return {
      token: localStorage.getItem("token") || "",
      refreshToken: localStorage.getItem("refresh_token") || "",
      user: null,
      userLoading: hasToken,
      licence: null,
      submissionUsage: null,
      navigationOpen: false,
      sidebarCollapsed: localStorage.getItem("sidebar_collapsed") === "true" || false,
      isExplanationVideoSubmitted: false,
      isExportVideoModalVisible: false,
      theme: "sensity_light",
      themePreference: localStorage.getItem("themePreference") || "auto",
      team: null,
      teamMembers: [],
      isTokenRefreshing: false,
      softwareVersion: `v${packageJson.version}`,
      integrations: {},
      products: [],
      productGroups: [],
      productGroupsLoading: false,
      productGroupsError: null,
      selectedDateFormat: localStorage.getItem("selectedDateFormat") || DEFAULT_DATE_FORMAT,
      selectedTimeFormat: localStorage.getItem("selectedTimeFormat") || DEFAULT_TIME_FORMAT,
    };
  },
  getters: {
    getUser: (state) => {
      if (!state.user) return {};

      let { user } = state;

      if (typeof user !== "object") {
        user = JSON.parse(user);
      }

      return user;
    },
    userDisplayName: (state, getters) => {
      const user = getters.getUser;
      if (getters.isStaffOnCloud) return user?.email || "";
      return user?.user_name || user?.email || "";
    },
    isUserLoading: (state) => state.userLoading,
    hasDetectionApiAccess: (state, getters) => {
      return getters.getUser?.detection_api_access || false;
    },
    getLicence: (state) => state.licence,
    getSubmissionUsage: (state) => state.submissionUsage,

    getLicenceExpiryDate: (state) => state.licence?.licence_expiry_date,
    isLicenceValid: (state) => state.licence?.is_licence_valid ?? false,
    getPackageType: (state) => state.licence?.package_type || "monthly",
    getLicenceLimits: (state) => state.licence?.limits,

    getUsed: (state) => state.submissionUsage?.used ?? 0,
    getLimit: (state) => state.submissionUsage?.limit,
    getPeriodStart: (state) => state.submissionUsage?.period_start,
    getPeriodType: (state) => state.submissionUsage?.period_type || "monthly",

    getRemainingCredits: (state, getters) => {
      const limit = getters.getLimit;
      const used = getters.getUsed;
      if (limit == null) return null;
      return Math.max(0, limit - used);
    },
    getUsagePercentage: (state, getters) => {
      const limit = getters.getLimit;
      const used = getters.getUsed;
      if (!limit) return 0;
      return Math.min(100, (used / limit) * 100);
    },

    isLimitExceeded: (state, getters) => {
      const limit = getters.getLimit;
      const used = getters.getUsed;

      return limit != null && used >= limit;
    },
    isSubscriptionExpired: (state, getters) => {
      if (getters.isOnPrem) return false;
      const { user } = state;
      if (!user) return false;
      if (user?.is_staff) return false;
      return !getters.isLicenceValid;
    },
    isSubmissionLimitReached: (state, getters) => {
      if (getters.isOnPrem) return false;
      const { user } = state;
      if (!user) return false;
      if (user?.is_staff) return false;
      return getters.isLimitExceeded;
    },
    isLicenseExpired: (state, getters) => {
      if (getters.isOnPrem) return false;
      const { user } = state;
      if (!user) return false;
      if (user?.is_staff) return false;
      return !getters.isLicenceValid || getters.isLimitExceeded;
    },
    getNavigationOpen: (state) => state.navigationOpen,
    isAuth: (state) => !!state.token.length,
    isStaging() {
      return config.VITE_APP_STAGING_ONLY === "on";
    },
    isOnPrem() {
      return config.VITE_APP_IS_ONPREM === "on";
    },
    isOnPremDesktop() {
      return config.VITE_APP_IS_ONPREM_DESKTOP === "on";
    },
    isDevMode() {
      return config.VITE_APP_DEV_MODE === "on";
    },
    isUrlSubmissionDisabled() {
      return config.VITE_APP_DISABLE_URL_SUBMISSIONS === "true";
    },
    bucketBaseUrl() {
      return config.VITE_APP_BUCKET_BASE_URL;
    },
    videoBasePath() {
      return config.VITE_APP_IS_ONPREM === "on"
        ? "/videos/"
        : "https://storage.googleapis.com/platform-dashboard-videos/";
    },
    isStaff: (state, getters) => {
      const user = getters.getUser;

      return user?.is_staff || false;
    },
    isStaffOnCloud: (state, getters) => {
      return getters.isStaff && !getters.isOnPrem;
    },
    isTeamAdmin: (state, getters) => {
      const user = getters.getUser;

      // /v2/users/me/ returns team_role as integer FK (1=Admin, 2=Analyst)
      // /v2/team/ members return team_role as string ("Admin", "Analyst")
      return user?.team_role === 1 || user?.team_role === "Admin" || false;
    },
    isTeamVisible: (state, getters) => {
      const user = getters.getUser;

      return user?.is_team_visible || false;
    },
    hasAccessToProductGroup: (state) => (product) => {
      // Check if user has access to a product by verifying if it exists in
      // the fetched productGroups array (which contains only user-accessible products).
      // This replaces the deprecated user.groups.product_access check.
      if (!product) {
        return false;
      }

      const { productGroups } = state;

      if (!productGroups || !Array.isArray(productGroups)) {
        return false;
      }

      return productGroups.some((pg) => pg && pg.name === product);
    },
    getExplanationVideoSubmittedStatus: (state) => state.isExplanationVideoSubmitted,
    getTheme: (state) => state.theme,
    getThemePreference: (state) => state.themePreference,
    getTeam: (state) => state.team,
    getTeamMembers: (state) => state.teamMembers,
    isCustomTheme: (state) => state.theme === "custom",
    isTokenRefreshing: (state) => state.isTokenRefreshing,
    softwareVersion: (state) => state.softwareVersion,
    getIntegrations: (state) => state.integrations,
    getSidebarCollapsed: (state) => state.sidebarCollapsed,
    getProducts: (state) => state.products,
    isExportVideoModalVisible: (state) => state.isExportVideoModalVisible,
    getProductGroups: (state) => state.productGroups || [],
    getProductGroupsLoading: (state) => state.productGroupsLoading,
    getProductGroupNames: (state) => {
      if (!state.productGroups || !Array.isArray(state.productGroups)) {
        return [];
      }
      return state.productGroups.map((pg) => pg?.name).filter(Boolean);
    },
    getSelectedDateFormat: (state) => state.selectedDateFormat,
    getSelectedTimeFormat: (state) => state.selectedTimeFormat,
    getDateFormatConfig: (state) => DATE_FORMATS[state.selectedDateFormat] || DATE_FORMATS[DEFAULT_DATE_FORMAT],
    getTimeFormatConfig: (state) => TIME_FORMATS[state.selectedTimeFormat] || TIME_FORMATS[DEFAULT_TIME_FORMAT],
  },
  mutations: {
    setTokens: (state, payload) => {
      if (payload.access) {
        localStorage.setItem("token", payload.access);
        state.token = payload.access;
        axios.defaults.headers.common.Authorization = `Bearer ${payload.access}`;
      }

      if (payload.refresh) {
        localStorage.setItem("refresh_token", payload.refresh);
        state.refreshToken = payload.refresh;
      }
    },
    setUser: (state, payload) => {
      state.user = payload;
    },
    setUserField: (state, { field, value }) => {
      if (state.user) {
        state.user[field] = value;
      }
    },
    setUserLoading: (state, payload) => {
      state.userLoading = payload;
    },
    setLicence: (state, payload) => {
      state.licence = payload;
    },
    setSubmissionUsage: (state, payload) => {
      state.submissionUsage = payload;
    },
    setNavigationOpen: (state, payload) => {
      state.navigationOpen = payload;
    },
    setSidebarCollapsed: (state, payload) => {
      state.sidebarCollapsed = payload;
      localStorage.setItem("sidebar_collapsed", payload.toString());
    },
    setExplanationVideoSubmittedStatus: (state, payload) => {
      state.isExplanationVideoSubmitted = payload;
    },
    setTheme: (state, payload) => {
      state.theme = payload;
    },
    setThemePreference: (state, payload) => {
      state.themePreference = payload;
    },
    setTeam: (state, payload) => {
      state.team = payload;
    },
    setTeamMembers: (state, payload) => {
      state.teamMembers = payload;
    },
    setTokenRefreshing: (state, payload) => {
      state.isTokenRefreshing = payload;
    },
    setIntegrations: (state, payload) => {
      state.integrations = payload;
    },
    setProducts: (state, payload) => {
      state.products = payload;
    },
    setExportVideoModalVisible: (state, payload) => {
      state.isExportVideoModalVisible = payload;
    },
    setProductGroups: (state, payload) => {
      state.productGroups = payload;
    },
    setProductGroupsLoading: (state, payload) => {
      state.productGroupsLoading = payload;
    },
    setProductGroupsError: (state, payload) => {
      state.productGroupsError = payload;
    },
    setSelectedDateFormat: (state, payload) => {
      state.selectedDateFormat = payload;
      localStorage.setItem("selectedDateFormat", payload);
    },
    setSelectedTimeFormat: (state, payload) => {
      state.selectedTimeFormat = payload;
      localStorage.setItem("selectedTimeFormat", payload);
    },
  },
  actions: {
    initializeApp({ state, commit, dispatch }) {
      // Return cached promise if already initializing/initialized
      if (initPromise) {
        return initPromise;
      }

      // If user has a token, fetch user data
      if (state.token) {
        initPromise = Promise.all([
          dispatch("fetchUser")
            .then(() => Promise.all([dispatch("fetchLicence"), dispatch("fetchUsage")]))
            .then(() => {
              setSentryUser(state.user);
            }),
          dispatch("fetchProductGroups"),
          dispatch("fetchProducts"),
        ]).catch((error) => {
          console.error("Failed to initialize app:", error);
          // Token likely invalid, clear auth state
          commit("setTokens", { access: "", refresh: "" });
          commit("setUser", null);
          commit("setUserLoading", false);
          clearSentryUser();
        });
      } else {
        // No token, resolve immediately
        commit("setUserLoading", false);
        initPromise = Promise.resolve();
      }

      return initPromise;
    },
    resetInitPromise() {
      // Reset init promise (useful after logout/login)
      initPromise = null;
      lastLicenceCheck = 0;
      lastUsageCheck = 0;
    },
    refreshLicenceIfStale({ dispatch, getters }) {
      if (getters.isOnPrem) return Promise.resolve();
      if (Date.now() - lastLicenceCheck > LICENCE_STALE_THRESHOLD) {
        return dispatch("fetchLicence");
      }
      return Promise.resolve();
    },
    refreshUsageIfStale({ dispatch }) {
      if (Date.now() - lastUsageCheck > LICENCE_STALE_THRESHOLD) {
        return dispatch("fetchUsage");
      }
      return Promise.resolve();
    },
    async login({ state, commit, dispatch }, payload) {
      const { data: response } = await authService.login(payload);
      const { access, refresh } = response;

      commit("setTokens", { access, refresh });

      axios.defaults.headers.common.Authorization = `Bearer ${access}`;

      // Reset init promise so it re-initializes with the new token
      dispatch("resetInitPromise");

      // Fetch user data before redirecting so route guards have access to user info
      try {
        await dispatch("fetchUser");
        await Promise.all([dispatch("fetchLicence"), dispatch("fetchUsage")]);
        await dispatch("fetchProductGroups", { forceRefresh: true });
        setSentryUser(state.user);
      } catch (error) {
        console.error("Failed to fetch user data:", error);
        commit("setTokens", { access: "", refresh: "" });

        throw error;
      }

      // Mark initialization as complete for subsequent navigations
      initPromise = Promise.resolve();

      // Get redirect URL from query parameter
      const redirectUrl = router.currentRoute.value.query.redirect;

      // Validate redirect URL to prevent open redirect vulnerabilities
      // Must be internal path (starts with /) and not protocol-relative (//)
      const isValidRedirect =
        redirectUrl && typeof redirectUrl === "string" && redirectUrl.startsWith("/") && !redirectUrl.startsWith("//");

      if (isValidRedirect) {
        router.push(redirectUrl);
      } else {
        router.push("/");
      }
    },
    logout({ commit, dispatch }) {
      return new Promise(() => {
        clearSentryUser();
        commit("setTokens", { access: "", refresh: "" });
        commit("setUser", null);
        commit("setUserLoading", false);
        commit("setProductGroups", []);
        commit("setProductGroupsLoading", false);
        commit("setProductGroupsError", null);
        commit("setTeamMembers", []);
        dispatch("resetInitPromise");
        localStorage.removeItem("token");
        localStorage.removeItem("refresh_token");
        localStorage.removeItem("deepfake-list-result");

        window.location.href = "/login";
      });
    },
    register(_, user) {
      return authService.register(user);
    },
    passwordRecovery(_, email) {
      return authService.passwordRecovery(email);
    },
    confirmNewPassword(_, { uid, token, password, confirmPassword }) {
      return authService.confirmNewPassword({
        uid,
        token,
        new_password1: password,
        new_password2: confirmPassword,
      });
    },
    changePassword(_, { passwordOld, passwordNew, passwordConfirm }) {
      return authService.changePassword({
        password_old: passwordOld,
        password_new: passwordNew,
        password_confirm: passwordConfirm,
      });
    },
    async fetchUser({ commit, dispatch }) {
      commit("setUserLoading", true);

      try {
        const { data: user } = await userService.fetchUser();
        commit("setUser", user);

        if (user.team) {
          await dispatch("fetchTeam");
        }
      } finally {
        commit("setUserLoading", false);
      }
    },
    async updateUserName({ commit }, userName) {
      await userService.updateUserName(userName);
      commit("setUserField", { field: "user_name", value: userName });
    },
    async fetchLicence({ commit, getters }) {
      if (getters.isOnPrem) return;

      try {
        const { data: licence } = await licenceService.fetchLicence();
        commit("setLicence", licence);
        lastLicenceCheck = Date.now();
      } catch (error) {
        console.error("Error fetching licence:", error);

        // Show user-friendly license errors here (e.g. quota exceeded, expired license),
        // unless mapLicenseError() returns suppressToast = true (quota exceeded errors are sometimes suppressed, not shown to the user)
        const { title, description, suppressToast } = mapLicenseError(error);
        if (!suppressToast) {
          toaster.error(title, {
            id: "license-error",
            duration: Infinity,
            description,
          });
        }
      }
    },
    async fetchUsage({ commit }) {
      try {
        const { data: usage } = await licenceService.fetchSubmissionUsage();
        commit("setSubmissionUsage", usage);
        lastUsageCheck = Date.now();
      } catch (error) {
        console.error("Error fetching submission usage:", error);

        const { title, description, suppressToast } = mapLicenseError(error);
        if (!suppressToast) {
          toaster.error(title, {
            id: "usage-error",
            duration: Infinity,
            description,
          });
        }
      }
    },
    async sendSlackMessage(_, { message, url, backendResponse = null }) {
      const response = await miscService.sendSlackMessage({ message, url, backendResponse });
      return response.data;
    },
    fetchSubmissionById(_, id) {
      return submissionService.fetchSubmissionById(id);
    },
    fetchTargetIndustries() {
      return miscService.fetchTargetIndustries();
    },
    sendContactEmail(_, params) {
      return miscService.sendContactEmail(params);
    },
    // v2 team management actions
    fetchTeamData() {
      return teamService.fetchTeamData();
    },
    updateTeamSettings(_, params) {
      return teamService.updateTeamSettings(params);
    },
    inviteTeamMember(_, { email }) {
      return teamService.inviteTeamMember(email);
    },
    updateTeamMemberRole(_, { memberId, teamRole }) {
      return teamService.updateTeamMemberRole(memberId, teamRole);
    },
    deactivateTeamMember(_, { memberId }) {
      return teamService.deactivateTeamMember(memberId);
    },
    // deprecated v1 team actions (kept for backwards compatibility)
    async fetchTeamMembers({ commit }) {
      const response = await teamService.fetchTeamMembers();
      commit("setTeamMembers", response.data?.results || []);
      return response;
    },
    fetchTeamRoles() {
      return teamService.fetchTeamRoles();
    },
    updateTeamMember(_, params) {
      return teamService.updateTeamMember(params);
    },
    inviteNewTeamMember(_, params) {
      return teamService.inviteNewTeamMember(params);
    },
    updateTeam(_, params) {
      return teamService.updateTeam(params);
    },
    async fetchTeam({ commit }) {
      const data = await teamService.fetchTeam();
      commit("setTeam", data.data);

      return data;
    },
    setExplanationVideoSubmittedStatus({ commit }, payload) {
      commit("setExplanationVideoSubmittedStatus", payload);
    },
    setTheme({ commit }, payload) {
      commit("setTheme", payload);
      localStorage.setItem("theme", payload);
    },
    setThemePreference({ commit }, payload) {
      commit("setThemePreference", payload);
      localStorage.setItem("themePreference", payload);
    },
    verifyToken() {
      return authService.verifyToken(localStorage.getItem("token"));
    },
    async refreshToken({ commit }) {
      if (!store.getters.isTokenRefreshing) {
        commit("setTokenRefreshing", true);

        try {
          const response = await authService.refreshToken(localStorage.getItem("refresh_token"));
          const { access } = response.data;

          commit("setTokens", { access });
          commit("setTokenRefreshing", false);

          return access;
        } catch (error) {
          console.error(`Error occurred refreshing token: ${error}`);
          commit("setTokenRefreshing", false);

          throw error;
        }
      }
    },
    async fetchIntegrations({ commit }) {
      try {
        const { data } = await integrationService.fetchIntegrations();
        commit("setIntegrations", data);
      } catch (error) {
        console.error("Error fetching integrations:", error);
      }
    },
    async disconnectIntegration({ commit }, key) {
      try {
        const { data } = await integrationService.disconnectIntegration(key);
        commit("setIntegrations", data);
      } catch (error) {
        console.error("Error disconnecting integration:", error);
      }
    },
    toggleSidebarCollapsed({ commit, state }) {
      commit("setSidebarCollapsed", !state.sidebarCollapsed);
    },
    setNavigationOpen({ commit }, payload) {
      commit("setNavigationOpen", payload);
    },
    submitDeepfakeDetection(_, data) {
      return submissionService.submitDeepfakeDetection(data);
    },
    fetchWebhooks(_, params = {}) {
      return webhookService.fetchWebhooks(params);
    },
    fetchWebhookById(_, webhookId) {
      return webhookService.fetchWebhookById(webhookId);
    },
    createWebhook(_, data) {
      return webhookService.createWebhook(data);
    },
    updateWebhook(_, { webhookId, data }) {
      return webhookService.updateWebhook(webhookId, data);
    },
    deleteWebhook(_, webhookId) {
      return webhookService.deleteWebhook(webhookId);
    },
    fetchApiKeys(_, params = {}) {
      return apiKeyService.fetchApiKeys(params);
    },
    createApiKey(_, data) {
      return apiKeyService.createApiKey(data);
    },
    deleteApiKey(_, apiKeyId) {
      return apiKeyService.deleteApiKey(apiKeyId);
    },
    async fetchProducts({ commit, state }, { forceRefresh = false, params = {} } = {}) {
      if (state.products.length > 0 && !forceRefresh) {
        return { data: { results: state.products } };
      }

      try {
        const response = await productService.fetchProducts(params);
        commit("setProducts", response.data.results);

        return response;
      } catch (error) {
        console.error("Error fetching products:", error);

        throw error;
      }
    },
    async fetchProductGroups({ commit, state }, { forceRefresh = false } = {}) {
      // Fetches product groups the current user has access to from the backend.
      // User access is determined by the product groups returned from this API,
      // NOT from user.groups.product_access (deprecated).
      if (state.productGroups.length > 0 && !forceRefresh) {
        return { data: { results: state.productGroups } };
      }

      commit("setProductGroupsLoading", true);
      commit("setProductGroupsError", null);

      try {
        const response = await productService.fetchProductGroups({ page_size: 100 });

        const productGroups = response.data.results || [];

        commit("setProductGroups", productGroups);
        commit("setProductGroupsLoading", false);

        return { data: { results: productGroups } };
      } catch (error) {
        console.error("Error fetching product groups:", error);
        commit("setProductGroupsError", error);
        commit("setProductGroupsLoading", false);

        commit("setProductGroups", []);

        return { data: { results: [] } };
      }
    },
    fetchWebhookEvents(_, params = {}) {
      return webhookService.fetchWebhookEvents(params);
    },
    fetchWebhookEventById(_, eventId) {
      return webhookService.fetchWebhookEventById(eventId);
    },
    retryWebhookEvent(_, eventId) {
      return webhookService.retryWebhookEvent(eventId);
    },
    async fetchArchiveVideoDetails(_, params) {
      const { data } = await mediaService.fetchArchiveVideoDetails(params);
      return data;
    },
    async fetchArchiveVideoDownloadUrl(_, jobId) {
      const { data } = await mediaService.fetchArchiveVideoDownloadUrl(jobId);
      return data;
    },
    async createArchiveVideo(_, data) {
      const { data: response } = await mediaService.createArchiveVideo(data);
      return response;
    },
    setExportVideoModalVisible({ commit }, payload) {
      commit("setExportVideoModalVisible", payload);
    },
    async fetchCsvExports(_, params = {}) {
      const { data } = await exportService.fetchCsvExports(params);
      return data;
    },
    async fetchCsvExportDownloadUrl(_, exportId) {
      const { data } = await exportService.fetchCsvExportDownloadUrl(exportId);
      return data;
    },
    async createCsvExport(_, filters = {}) {
      const { data } = await exportService.createCsvExport(filters);
      return data;
    },
  },
  modules: {
    upload,
    comments,
    submission,
    list,
  },
});

export default store;
